import React, { useContext } from 'react';
import { ConfigContext } from 'src/components/AppShell/ConfigProvider';
import { SplitTreatments } from '@splitsoftware/splitio-react';
import { toNumber } from 'es-toolkit/compat';
import { useHolidaysAndBlackDays } from 'src/hooks/useHolidaysAndBlackDays';
import { shippingGroup_shippingGroup_items_attribute_options } from 'src/queries/__generated__/shippingGroup';
import { SplitReadyContext } from 'src/components/Splitio/SplitReadyContext';
import { SplitScope } from 'src/components/Splitio/SplitScope';
import { useRenderEnv } from 'src/hooks/useBreakpoint';

import { MainContext } from '../Main/Main';

import {
    timeLeft,
    timePlusDays,
    getDeliveryMethods,
    extractDaysFromDeliveryMethod,
    shouldShowNextDayDeliveryMessage,
} from './utils/productTimeDeliverySettings';
import classes from './ProductTimeDelivery.scss';

type ProductTimeDeliveryProps = {
    shippingTypes: string[];
    isCustomisable: boolean;
    productUrl: string;
    cutOffTime: string[];
    superFastCutOffTime?: string[];
};

const featureName = 'Express_Delivery';

const shouldDisableDropshipping = (
    shippingTypes: string[],
    shippingGroupList: shippingGroup_shippingGroup_items_attribute_options[],
): boolean => {
    const uniqueShippingCodes = Array.from(new Set(shippingTypes));

    const labelsForCodes = uniqueShippingCodes.map(
        (code) => shippingGroupList.find((group) => group.value === code)?.label,
    );

    if (labelsForCodes.length === 0) return true;

    const allEndWith24 = labelsForCodes.every((label) => Boolean(label) && label?.trim().toLowerCase().includes('24'));

    return !allEndWith24;
};

export const ProductTimeDelivery: React.FC<ProductTimeDeliveryProps> = React.memo(
    ({ shippingTypes, isCustomisable, productUrl, cutOffTime, superFastCutOffTime }) => {
        const today = new Date();
        const { isBrowser } = useRenderEnv();
        const { shippingGroupList } = useContext(MainContext);
        const { additionalDays } = useContext(ConfigContext);
        const [cutOffTimeHours, cutOffTimeMinutes] = cutOffTime;
        const [superFastCutOffTimeHours, superFastCutOffTimeMinutes] =
            superFastCutOffTime && superFastCutOffTime.length > 0 ? superFastCutOffTime : cutOffTime;

        const { showExpress, sameDayDelivery } = getDeliveryMethods(shippingTypes, shippingGroupList);
        const showNextDayDeliveryMessage = shouldShowNextDayDeliveryMessage(shippingTypes, shippingGroupList);

        const { GBHolidaysAndBlackDays, filteredGBHolidaysAndBlackDays } = useHolidaysAndBlackDays();

        const noDropshipping = shouldDisableDropshipping(shippingTypes, shippingGroupList);

        const countdownTime = timeLeft(Number(cutOffTimeHours) - 1, Number(cutOffTimeMinutes));

        return (
            <div className={classes.container}>
                <div className={classes.nextDayBanner}>
                    <span className={classes.nextDayBannerIcon}>
                        <img src={require('src/components/Svg/svgs/lightning-bolt.svg')} alt="Lightning bolt icon" />
                    </span>
                    Order within the next{' '}
                    <span className={classes.nextDayBannerTime}>
                        {isBrowser ? countdownTime : <TimePlaceholder text="00hrs 00 mins" />}
                        {showNextDayDeliveryMessage && ' for Next Day Delivery'}
                    </span>
                </div>
                <DeliveryTime
                    title="Standard Delivery"
                    pending={!isBrowser}
                    standardDate={
                        timePlusDays(
                            extractDaysFromDeliveryMethod(shippingGroupList, shippingTypes, noDropshipping),
                            GBHolidaysAndBlackDays,
                            Number(cutOffTimeHours),
                            today,
                            Number(cutOffTimeMinutes),
                        ).formatedDate
                    }
                    additionalDate={
                        isCustomisable
                            ? timePlusDays(
                                  extractDaysFromDeliveryMethod(shippingGroupList, shippingTypes, true) +
                                      additionalDays,
                                  filteredGBHolidaysAndBlackDays,
                                  Number(cutOffTimeHours),
                                  today,
                                  Number(cutOffTimeMinutes),
                              ).formatedDate
                            : undefined
                    }
                />

                <ExpressDeliverySplit
                    productUrl={productUrl}
                    showExpress={Boolean(showExpress)}
                    isCustomisable={isCustomisable}
                    shippingTypes={shippingTypes}
                    shippingGroupList={shippingGroupList}
                    noDropshipping={noDropshipping}
                    filteredGBHolidaysAndBlackDays={filteredGBHolidaysAndBlackDays}
                    superFastCutOffTimeHours={superFastCutOffTimeHours}
                    superFastCutOffTimeMinutes={superFastCutOffTimeMinutes}
                    today={today}
                />

                {sameDayDelivery && isCustomisable && (
                    <DeliveryTime
                        title="Same Day Dispatch"
                        tag="TheFastest"
                        pending={!isBrowser}
                        additionalDate={
                            timePlusDays(1, filteredGBHolidaysAndBlackDays, toNumber(cutOffTimeHours), today)
                                .formatedDate
                        }
                    />
                )}
            </div>
        );
    },
);

type ExpressDeliverySplitProps = {
    productUrl: string;
    showExpress: boolean;
    isCustomisable: boolean;
    shippingTypes: string[];
    shippingGroupList: shippingGroup_shippingGroup_items_attribute_options[];
    noDropshipping: boolean;
    filteredGBHolidaysAndBlackDays: any[];
    superFastCutOffTimeHours: string;
    superFastCutOffTimeMinutes: string;
    today: Date;
};

const ExpressDeliverySplit: React.FC<ExpressDeliverySplitProps> = ({
    productUrl,
    showExpress,
    isCustomisable,
    shippingTypes,
    shippingGroupList,
    noDropshipping,
    filteredGBHolidaysAndBlackDays,
    superFastCutOffTimeHours,
    superFastCutOffTimeMinutes,
    today,
}) => {
    const splitReady = useContext(SplitReadyContext);
    if (!splitReady) return null;

    return (
        <SplitScope>
            <SplitTreatments names={[featureName]} attributes={{ path: `/${productUrl}` }}>
                {({ treatments, isReady }) => {
                    const { treatment } = treatments[featureName];
                    return isReady && treatment === 'on' && showExpress && isCustomisable ? (
                        <DeliveryTime
                            title="SuperFast Delivery"
                            additionalDate={
                                timePlusDays(
                                    extractDaysFromDeliveryMethod(shippingGroupList, shippingTypes, noDropshipping, {
                                        preferLeadingDaysBefore24: true,
                                    }) + 1, //+ 1 day on delivery day for customisation
                                    filteredGBHolidaysAndBlackDays,
                                    Number(superFastCutOffTimeHours),
                                    today,
                                    Number(superFastCutOffTimeMinutes),
                                ).formatedDate
                            }
                        />
                    ) : null;
                }}
            </SplitTreatments>
        </SplitScope>
    );
};

type containerProps = {
    title: string;
    standardDate?: string;
    additionalDate?: string;
    tag?: string;
    pending?: boolean;
};

const DATE_PLACEHOLDER = 'Wednesday 30th September';

const TimePlaceholder: React.FC<{ text: string }> = ({ text }) => (
    <span className={classes.placeholder} aria-hidden="true">
        {text}
    </span>
);

export const DeliveryTime: React.FC<containerProps> = ({ title, standardDate, additionalDate, tag, pending }) => (
    <div className={classes.deliveryTimeContainer}>
        <div className={classes.titleContainer}>
            <span className={classes.deliveryMethodName}>{title}</span>
            {tag && <img className={classes.fastTag} src={`../../../static/icons/${tag}.svg`} alt="" />}
            <img src="../../../static/icons/fedex.svg" className={classes.logo} alt="FedEx" />
        </div>
        {standardDate && (
            <p className={classes.info}>
                Without Logo: Estimated arrival{' '}
                <b className={classes.deliveryTimeDate}>
                    {pending ? <TimePlaceholder text={DATE_PLACEHOLDER} /> : standardDate}
                </b>
            </p>
        )}
        {additionalDate && (
            <p className={classes.info}>
                With Logo: Estimated arrival{' '}
                <b className={classes.deliveryTimeDate}>
                    {pending ? <TimePlaceholder text={DATE_PLACEHOLDER} /> : additionalDate}
                </b>
            </p>
        )}
    </div>
);
